(() => {
  'use strict';
  const t = (text, params) => window.LRSI18n?.t(text, params) ?? String(text).replace(/\{(\w+)\}/g, (match, key) => params && Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match);
  if (window.LRSLayeredStories) return;
  const instances = new Map();
  function init(root = document) {
    const sections = [...root.querySelectorAll('[data-layered-stories]')];
    if (root.matches?.('[data-layered-stories]')) sections.unshift(root);
    sections.forEach(section => {
      if (instances.has(section)) return;
      const slides = [...section.querySelectorAll('[data-layered-slide]')];
      if (slides.length < 2) return;
      const stage = section.querySelector('[data-layered-stage]');
      const controls = section.querySelector('[data-layered-controls]');
      const choices = [...section.querySelectorAll('[data-layered-choice]')];
      const count = section.querySelector('[data-layered-count]');
      const status = section.querySelector('[data-layered-status]');
      const playback = section.querySelector('[data-layered-playback]');
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const autoplay = section.dataset.autoplay !== 'false';
      const interval = Math.min(12, Math.max(5, Number(section.dataset.autoplayInterval) || 5)) * 1000;
      const editing = Boolean(window.Shopify?.designMode);
      const abort = new AbortController();
      const savedTabs = new Map();
      const listen = (node, event, fn, options = {}) => node?.addEventListener(event, fn, {...options, signal: abort.signal});
      let current = 0;
      let pointer;
      let suppressClickUntil = 0;
      let frame = 0;
      let elapsed = 0;
      let started = 0;
      let inView = !('IntersectionObserver' in window);
      let focused = section.contains(document.activeElement);
      let pointerFocus = false;
      let userPaused = false;
      let playThroughFocus = false;
      let destroyed = false;
      const pad = number => String(number).padStart(2, '0');
      const allowsMotion = () => !motion.matches && document.body?.dataset.lrsMotion !== 'false';
      const canAutoplay = () => autoplay && allowsMotion() && !editing;
      const shouldRun = () => canAutoplay() && inView && !document.hidden && (!focused || playThroughFocus) && !userPaused && !pointer;
      function stop() {
        if (!frame) return;
        elapsed = Math.min(interval, elapsed + performance.now() - started);
        cancelAnimationFrame(frame);
        frame = 0;
      }
      function tick(now) {
        frame = 0;
        if (destroyed) return;
        elapsed += now - started;
        started = now;
        if (elapsed >= interval) {
          elapsed = 0;
          show(current + 1, false);
        }
        if (shouldRun()) frame = requestAnimationFrame(tick);
      }
      function syncPlayback() {
        if (destroyed) return;
        if (!shouldRun()) stop();
        else if (!frame) {
          started = performance.now();
          frame = requestAnimationFrame(tick);
        }
        if (playback) {
          playback.hidden = !canAutoplay();
          playback.textContent = userPaused ? t('Play') : t('Pause');
          playback.setAttribute('aria-label', userPaused ? t('Play automatic stories') : t('Pause automatic stories'));
        }
        section.classList.toggle('is-playing', Boolean(frame));
      }
      function show(index, announce = true) {
        const next = (index + slides.length) % slides.length;
        if (announce && slides[current].contains(document.activeElement)) stage.focus({preventScroll: true});
        current = next;
        slides.forEach((slide, i) => {
          const active = i === current;
          slide.classList.toggle('is-active', active);
          slide.toggleAttribute('inert', !active);
          slide.setAttribute('aria-hidden', String(!active));
          // tabindex also supports browsers without native inert.
          slide.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach(node => {
            if (!savedTabs.has(node)) savedTabs.set(node, node.getAttribute('tabindex'));
            const original = savedTabs.get(node);
            if (!active) node.setAttribute('tabindex', '-1');
            else if (original === null) node.removeAttribute('tabindex');
            else node.setAttribute('tabindex', original);
          });
          choices[i]?.setAttribute('aria-pressed', String(active));
        });
        count.textContent = `${pad(current + 1)} / ${pad(slides.length)}`;
        if (announce) status.textContent = t('{label}, {current} of {total}', {label:choices[current].textContent.trim(), current:current + 1, total:slides.length});
      }
      function navigate(index) {
        stop();
        elapsed = 0;
        show(index);
        syncPlayback();
      }
      section.classList.add('is-enhanced');
      section.setAttribute('aria-roledescription', t('carousel'));
      stage.tabIndex = 0;
      stage.setAttribute('role', 'group');
      stage.setAttribute('aria-label', t('Image stories. Use left and right arrow keys to explore.'));
      controls.hidden = false;
      show(0, false);
      choices.forEach((button, index) => listen(button, 'click', () => navigate(index)));
      listen(section.querySelector('[data-layered-prev]'), 'click', () => navigate(current - 1));
      listen(section.querySelector('[data-layered-next]'), 'click', () => navigate(current + 1));
      listen(playback, 'click', () => {
        userPaused = !userPaused;
        playThroughFocus = !userPaused;
        syncPlayback();
      });
      listen(section, 'keydown', event => {
        if (event.target !== stage && !controls.contains(event.target)) return;
        const keys = {ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: slides.length - 1};
        if (!(event.key in keys) || event.altKey || event.ctrlKey || event.metaKey) return;
        event.preventDefault();
        navigate(keys[event.key]);
      });
      listen(document, 'keydown', () => {
        pointerFocus = false;
        if (!section.contains(document.activeElement)) return;
        focused = true;
        playThroughFocus = false;
        syncPlayback();
      }, {capture: true});
      listen(section, 'pointerdown', () => {
        // Mouse, pen and touch navigation retain automatic playback. A later
        // keyboard event switches back to a reading pause before focus moves.
        pointerFocus = true;
        focused = false;
        playThroughFocus = false;
        syncPlayback();
      }, {passive: true});
      listen(section, 'focusin', () => {
        focused = !pointerFocus;
        playThroughFocus = false;
        syncPlayback();
      });
      listen(section, 'focusout', event => {
        if (section.contains(event.relatedTarget)) return;
        focused = false;
        playThroughFocus = false;
        syncPlayback();
      });
      listen(stage, 'pointerdown', event => {
        if (event.isPrimary === false || (event.button !== undefined && event.button !== 0) || event.target.closest('a,button,input,select,textarea')) return;
        pointer = {id: event.pointerId, x: event.clientX, y: event.clientY};
        syncPlayback();
      }, {passive: true});
      listen(window, 'pointerup', event => {
        if (!pointer || pointer.id !== event.pointerId) return;
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        pointer = null;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) { syncPlayback(); return; }
        suppressClickUntil = performance.now() + 350;
        navigate(current + (dx < 0 ? 1 : -1));
      }, {passive: true});
      listen(window, 'pointercancel', () => { pointer = null; syncPlayback(); }, {passive: true});
      listen(stage, 'dragstart', event => event.preventDefault());
      listen(stage, 'click', event => {
        if (performance.now() < suppressClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, {capture: true});
      listen(section, 'shopify:block:select', event => {
        const index = slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId || slide === event.target.closest('[data-layered-slide]'));
        if (index !== -1) navigate(index);
      });
      listen(document, 'visibilitychange', syncPlayback);
      listen(motion, 'change', syncPlayback);
      const preferenceObserver = new MutationObserver(syncPlayback);
      preferenceObserver.observe(document.body, {attributes: true, attributeFilter: ['data-lrs-motion']});
      const viewObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        inView = entries[0].isIntersecting && entries[0].intersectionRatio >= .15;
        syncPlayback();
      }, {threshold: [0, .15]}) : null;
      viewObserver?.observe(section);
      syncPlayback();
      instances.set(section, () => {
        destroyed = true;
        stop();
        abort.abort();
        viewObserver?.disconnect();
        preferenceObserver.disconnect();
        section.classList.remove('is-enhanced', 'is-playing');
        section.removeAttribute('aria-roledescription');
        stage.removeAttribute('tabindex');
        stage.removeAttribute('role');
        stage.removeAttribute('aria-label');
        controls.hidden = true;
        if (playback) playback.hidden = true;
        slides.forEach(slide => { slide.removeAttribute('inert'); slide.removeAttribute('aria-hidden'); slide.classList.remove('is-active'); });
        savedTabs.forEach((value, node) => value === null ? node.removeAttribute('tabindex') : node.setAttribute('tabindex', value));
        status.textContent = '';
        instances.delete(section);
      });
    });
  }
  window.LRSLayeredStories = {init};
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, section) => { if (event.target === section || event.target.contains(section)) destroy(); });
  });
})();
